import React, { useEffect, useRef } from 'react';
import { defaultFilters, useFeedStore } from '@/app/store/feedStore';
import { useAuthStore } from '@/app/store/authStore';
import { BUSINESS_SEGMENTS, GetFeedItemsQuery, ProductType, REVIEW_STATUS_OPTIONS, ReviewStatus } from '@/shared/types/api';
import {
  SearchIcon,
  StarIcon,
  ExternalLinkIcon,
  ClockIcon,
  Building2,
  Layers,
  RefreshCwIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InboxIcon,
  XIcon,
  UserCheck,
} from 'lucide-react';
import { FeedItemActions } from './FeedItemActions';
import { ClassificationBadge } from '@/shared/components/classification/ClassificationBadge';
import { ComplexityBadge } from '@/shared/components/classification/ComplexityBadge';
import { ImpactBadge } from '@/shared/components/classification/ImpactBadge';
import { SegmentBadge } from '@/shared/components/classification/SegmentBadge';
import { getSegmentMeta } from '@/shared/lib/classificationMeta';
import { EmptyState } from '@/shared/components/EmptyState';
import { ExpandableText } from '@/shared/components/ExpandableText';
import { OverflowList } from '@/shared/components/OverflowList';
import { ItemTagChip } from '@/shared/components/ItemTagChip';
import { TagSwatch } from '@/shared/components/TagChip';
import { cn } from '@/shared/utils/cn';
import { formatDate, formatDateTime, formatRelative } from '@/shared/utils/date';
import { todayIso } from '@/shared/utils/today';
import { formatUserLabel } from '@/shared/utils/user';
import toast from 'react-hot-toast';

export const FeedList: React.FC = () => {
  const { user } = useAuthStore();
  const {
    items,
    totalCount,
    currentPage,
    pageSize,
    isLoading,
    isClassifying,
    error,
    filters,
    productTypes,
    searchTerm,
    selectedProductType,
    showUnreadOnly,
    showStarredOnly,
    selectedSegments,
    selectedTags,
    selectedClientIds,
    dateFrom,
    dateTo,
    availableTags,
    tagMeta,
    clients,
    selectedComplexity,
    selectedImpact,
    selectedReleaseType,
    availableReleaseTypes,
    showMultiVersion,
    showDeleted,
    selectedAssigneeIds,
    selectedReviewStatus,
    users,
    loadFilterOptions,
    markItemAsRead,
    markItemAsStarred,
    setSearchFilters,
    setFilters,
    clearError,
    loadProductTypes
  } = useFeedStore();

  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const buildFilterQueryFromUiState = (): Partial<GetFeedItemsQuery> => ({
    searchTerm,
    productType: selectedProductType || undefined,
    isRead: showUnreadOnly ? false : undefined,
    isStarred: showStarredOnly ? true : undefined,
    segments: selectedSegments.length ? selectedSegments : undefined,
    // SC-242. The date inputs give a plain yyyy-MM-dd; endDate is pushed to the end of the
    // chosen day so "to 5 Aug" includes items published during 5 Aug.
    tags: selectedTags.length ? selectedTags : undefined,
    clientIds: selectedClientIds.length ? selectedClientIds : undefined,
    startDate: dateFrom ? `${dateFrom}T00:00:00` : undefined,
    endDate: dateTo ? `${dateTo}T23:59:59` : undefined,
    complexity: selectedComplexity || undefined,
    impact: selectedImpact || undefined,
    releaseType: selectedReleaseType || undefined,
    multiVersion: showMultiVersion ? true : undefined,
    deleted: showDeleted ? true : undefined,
    // SC-423. Review status is judged on the viewer's own day, like the dashboard's Today tile.
    assignedUserId: selectedAssigneeIds.length ? selectedAssigneeIds : undefined,
    reviewStatus: selectedReviewStatus || undefined,
    today: selectedReviewStatus ? todayIso() : undefined,
  });

  // Load the product-type and tag/client vocabularies once on mount. The feed items
  // themselves are loaded by the real-time filter effect below (SC-295).
  useEffect(() => {
    loadProductTypes();
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SC-295: apply filters in real time — there is no longer an "Apply" button. A short
  // debounce coalesces rapid changes (typing in the search box, toggling several chips) into
  // a single request, and any filter change resets to page 1. This effect also performs the
  // initial load, and picks up filter state seeded by a dashboard card before navigation.
  // The initial load fires immediately (no 250ms flash of empty results); only subsequent
  // filter changes are debounced (review L3).
  const firstLoad = useRef(true);
  useEffect(() => {
    const delay = firstLoad.current ? 0 : 250;
    firstLoad.current = false;
    const handle = setTimeout(() => {
      setFilters({
        page: 1,
        pageSize: defaultFilters.pageSize,
        ...buildFilterQueryFromUiState(),
      });
    }, delay);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    selectedProductType,
    showUnreadOnly,
    showStarredOnly,
    selectedSegments,
    selectedTags,
    selectedClientIds,
    dateFrom,
    dateTo,
    selectedComplexity,
    selectedImpact,
    selectedReleaseType,
    showMultiVersion,
    showDeleted,
    selectedAssigneeIds,
    selectedReviewStatus,
  ]);

  const handleClearFilters = () => {
    // Reset the retained UI state; the real-time effect above applies the cleared query.
    setSearchFilters({
      searchTerm: '',
      selectedProductType: '',
      showUnreadOnly: false,
      showStarredOnly: false,
      selectedSegments: [],
      selectedTags: [],
      selectedClientIds: [],
      dateFrom: '',
      dateTo: '',
      selectedComplexity: '',
      selectedImpact: '',
      selectedReleaseType: '',
      showMultiVersion: false,
      showDeleted: false,
      selectedAssigneeIds: [],
      selectedReviewStatus: '',
    });
  };

  const userName = (id: string) => {
    const match = users.find((u) => u.id === id);
    if (!match) return 'Unknown user';
    return formatUserLabel(match);
  };

  const reviewStatusLabel = (status: ReviewStatus) =>
    REVIEW_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;

  const toggleSegmentFilter = (segment: (typeof BUSINESS_SEGMENTS)[number]) => {
    setSearchFilters({
      selectedSegments: selectedSegments.includes(segment)
        ? selectedSegments.filter((s) => s !== segment)
        : [...selectedSegments, segment],
    });
  };

  const toggleTagFilter = (tag: string) => {
    setSearchFilters({
      selectedTags: selectedTags.includes(tag)
        ? selectedTags.filter((t) => t !== tag)
        : [...selectedTags, tag],
    });
  };

  const tagFilterOptions = [...availableTags, ...selectedTags.filter((t) => !availableTags.includes(t))];

  const toggleClientFilter = (clientId: number) => {
    setSearchFilters({
      selectedClientIds: selectedClientIds.includes(clientId)
        ? selectedClientIds.filter((c) => c !== clientId)
        : [...selectedClientIds, clientId],
    });
  };

  const handleMarkAsRead = async (itemId: number, isRead: boolean) => {
    if (!user) return;

    const success = await markItemAsRead(itemId, user.userId, isRead);
    if (success) {
      toast.success(`Marked as ${isRead ? 'read' : 'unread'}`);
    }
  };

  const handleMarkAsStarred = async (itemId: number, isStarred: boolean) => {
    if (!user) return;

    const success = await markItemAsStarred(itemId, user.userId, isStarred);
    if (success) {
      toast.success(`${isStarred ? 'Starred' : 'Unstarred'} item`);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const getProductTypeInfo = (productType: ProductType) => {
    return productTypes.find(pt => pt.value === productType);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">RSS Feeds</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Browse and manage your Fenergo documentation updates
        </p>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Search */}
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchFilters({ searchTerm: e.target.value })}
              className="input pl-10"
            />
          </div>

          {/* Product Type Filter */}
          <select
            value={selectedProductType}
            onChange={(e) => setSearchFilters({ selectedProductType: e.target.value as ProductType | '' })}
            className="input"
          >
            <option value="">All Item Types</option>
            {productTypes.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.displayName ?? pt.name}
              </option>
            ))}
          </select>

          {/* Quick Filters — independent toggles styled as a segmented group */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1">
            <button
              type="button"
              onClick={() => setSearchFilters({ showUnreadOnly: !showUnreadOnly })}
              aria-pressed={showUnreadOnly}
              className={cn(
                'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                showUnreadOnly
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Unread
            </button>
            <button
              type="button"
              onClick={() => setSearchFilters({ showStarredOnly: !showStarredOnly })}
              aria-pressed={showStarredOnly}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                showStarredOnly
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <StarIcon className={cn('h-3.5 w-3.5', showStarredOnly && 'fill-warning text-warning')} />
              Starred
            </button>
          </div>

          {/* Clear — filters apply in real time (SC-295), so there is no Apply button. */}
          <div className="flex gap-2">
            <button onClick={handleClearFilters} className="btn-secondary flex-1">
              Clear filters
            </button>
          </div>
        </div>

        {/* Business Segment Filter — compact chip-toggle row */}
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Segments:</span>
          {BUSINESS_SEGMENTS.map((segment) => {
            const meta = getSegmentMeta(segment);
            const isSelected = selectedSegments.includes(segment);
            return (
              <button
                key={segment}
                type="button"
                onClick={() => toggleSegmentFilter(segment)}
                aria-pressed={isSelected}
                title={meta.tooltip}
                className={cn(
                  'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        {/* Client filter — multiselect (SC-242) */}
        {clients.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Clients:</span>
            {clients.map((client) => {
              const isSelected = selectedClientIds.includes(client.id);
              return (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => toggleClientFilter(client.id)}
                  aria-pressed={isSelected}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {client.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Tag filter — multiselect (SC-242), in the admin's order and colours (SC-376). A
            retained selection of a since-removed tag stays visible so it can be cleared. */}
        {tagFilterOptions.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Tags:</span>
            {tagFilterOptions.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const isRemoved = !availableTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTagFilter(tag)}
                  aria-pressed={isSelected}
                  title={isRemoved ? 'Removed tag - click to clear this filter' : tagMeta[tag]?.description}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground',
                    isRemoved && 'border-dashed italic'
                  )}
                >
                  <TagSwatch color={tagMeta[tag]?.color} />
                  {tag}
                </button>
              );
            })}
          </div>
        )}

        {/* Release Type filter (SC-301) */}
        {availableReleaseTypes.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Release Type:</span>
            <select
              value={selectedReleaseType}
              onChange={(e) => setSearchFilters({ selectedReleaseType: e.target.value })}
              className="input h-8 max-w-xs py-1 text-xs"
              aria-label="Release Type"
            >
              <option value="">All release types</option>
              {availableReleaseTypes.map((rt) => (
                <option key={rt} value={rt}>
                  {rt}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Assignment filters (SC-423). Assigned to lists every user, inactive included, so
            assignments left on deactivated users can be found; picked users become chips. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Assigned to:</span>
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                setSearchFilters({ selectedAssigneeIds: [...selectedAssigneeIds, e.target.value] });
              }
            }}
            className="input h-8 max-w-xs py-1 text-xs"
            aria-label="Assigned to User"
          >
            <option value="">{selectedAssigneeIds.length ? 'Add another user…' : 'Any user'}</option>
            {users
              .filter((u) => !selectedAssigneeIds.includes(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {formatUserLabel(u)}
                </option>
              ))}
          </select>
          <span className="text-xs font-medium text-muted-foreground">Review by:</span>
          <select
            value={selectedReviewStatus}
            onChange={(e) => setSearchFilters({ selectedReviewStatus: e.target.value as ReviewStatus | '' })}
            className="input h-8 max-w-xs py-1 text-xs"
            aria-label="Review by Date"
          >
            <option value="">Any review date</option>
            {REVIEW_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} title={o.description}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {(selectedAssigneeIds.length > 0 || selectedReviewStatus) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {selectedAssigneeIds.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() =>
                  setSearchFilters({ selectedAssigneeIds: selectedAssigneeIds.filter((x) => x !== id) })
                }
                className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                aria-label={`Remove assigned-to filter ${userName(id)}`}
              >
                <UserCheck className="h-3 w-3" />
                {userName(id)}
                <XIcon className="h-3 w-3" />
              </button>
            ))}
            {selectedReviewStatus && (
              <button
                type="button"
                onClick={() => setSearchFilters({ selectedReviewStatus: '' })}
                className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                aria-label={`Remove review-by filter ${reviewStatusLabel(selectedReviewStatus)}`}
              >
                Review by: {reviewStatusLabel(selectedReviewStatus)}
                <XIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Version / deletion filters (SC-278 / SC-301) */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Versions:</span>
          <button
            type="button"
            onClick={() => setSearchFilters({ showMultiVersion: !showMultiVersion })}
            aria-pressed={showMultiVersion}
            title="Show only items that have been revised (more than one version)"
            className={cn(
              'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
              showMultiVersion
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            Multi-version
          </button>
          <button
            type="button"
            onClick={() => setSearchFilters({ showDeleted: !showDeleted })}
            aria-pressed={showDeleted}
            title="Show items removed from the feed (deleted)"
            className={cn(
              'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
              showDeleted
                ? 'border-destructive bg-destructive/10 text-destructive'
                : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            Deleted
          </button>
        </div>

        {/* Classification filter arriving from a dashboard card (SC-234). Shown as a
            dismissible chip because there is no control for it in this panel. */}
        {(selectedComplexity || selectedImpact) && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">From dashboard:</span>
            {selectedComplexity && (
              <button
                type="button"
                onClick={() => setSearchFilters({ selectedComplexity: '' })}
                className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                aria-label={`Remove complexity filter ${selectedComplexity}`}
              >
                Complexity: {selectedComplexity}
                <XIcon className="h-3 w-3" />
              </button>
            )}
            {selectedImpact && (
              <button
                type="button"
                onClick={() => setSearchFilters({ selectedImpact: '' })}
                className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                aria-label={`Remove impact filter ${selectedImpact}`}
              >
                Impact: {selectedImpact}
                <XIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Date range filter (SC-242) */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Published:</span>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>From</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setSearchFilters({ dateFrom: e.target.value })}
              className="input h-8 py-1 text-xs"
              aria-label="Published from"
            />
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>To</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setSearchFilters({ dateTo: e.target.value })}
              className="input h-8 py-1 text-xs"
              aria-label="Published to"
            />
          </label>
        </div>
      </div>

      {/* Results */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border p-4">
          <h3 className="text-lg font-semibold text-foreground">
            Feed Items{' '}
            <span className="text-base font-normal tabular-nums text-muted-foreground">
              ({totalCount})
            </span>
          </h3>
          {isLoading && <RefreshCwIcon className="h-5 w-5 animate-spin text-muted-foreground" />}
        </div>

        {isLoading ? (
          <ul className="divide-y divide-border">
            {[...Array(6)].map((_, i) => (
              <li key={i} className="flex items-start gap-3 p-4">
                <div className="skeleton mt-1.5 h-2 w-2 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 w-3/4" />
                  <div className="skeleton h-3 w-1/2" />
                  <div className="skeleton h-3 w-full" />
                </div>
              </li>
            ))}
          </ul>
        ) : items.length > 0 ? (
          <>
            <ul className="divide-y divide-border">
              {items.map((item) => {
                const productTypeInfo = getProductTypeInfo(item.productType);
                return (
                  <li
                    key={item.id}
                    className={cn(
                      'p-4 transition-colors hover:bg-muted/50',
                      !item.isRead && 'bg-primary/[0.04]'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread gutter dot (width reserved when read) */}
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
                          item.isRead ? 'bg-transparent ring-1 ring-border' : 'bg-primary'
                        )}
                      />

                      {/* Main content */}
                      <div className="min-w-0 flex-1">
                        <h4
                          className={cn(
                            'flex flex-wrap items-center gap-x-2 gap-y-1',
                            item.isRead
                              ? 'text-sm font-medium text-muted-foreground'
                              : 'text-[15px] font-semibold text-foreground'
                          )}
                        >
                          <span>{item.title}</span>
                          {/* SC-278: removed-from-feed badge */}
                          {item.isDeleted && (
                            <span className="rounded border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                              Deleted
                            </span>
                          )}
                          {/* SC-278: revised-since-publish indicator */}
                          {(item.versionCount ?? 1) > 1 && (
                            <span
                              className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
                              title={`Revised — ${item.versionCount} versions`}
                            >
                              v{item.versionCount}
                            </span>
                          )}
                        </h4>

                        {/* Meta line — feed source + date only, no chips */}
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/80">{item.feedName}</span>
                          <span aria-hidden="true">·</span>
                          {/* SC-281: the feed shows the item's actual date; elapsed time
                              ("2 months ago") moves to the tooltip. */}
                          <span
                            className="inline-flex items-center gap-1"
                            title={`${formatDateTime(item.publishedDate)} (${formatRelative(item.publishedDate)})`}
                          >
                            <ClockIcon className="h-3 w-3" />
                            {formatDate(item.publishedDate)}
                          </span>
                        </div>

                        {/* Attribute line — impact/complexity, segments, tags, grouped as clusters */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          {productTypeInfo && (
                            <span className="chip-tag">{productTypeInfo.displayName ?? productTypeInfo.name}</span>
                          )}
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
                            <ImpactBadge value={item.potentialImpact} isClassifying={isClassifying} />
                            <ComplexityBadge value={item.complexity} isClassifying={isClassifying} />
                            {/* RF-91: provenance sits next to the badges it qualifies; renders
                                nothing when the item has no classification metadata. The
                                revision timestamp lets the chip flag a verdict that predates
                                the content it is describing (SC-278). */}
                            <ClassificationBadge
                              classification={item.classification}
                              lastVersionedAt={item.lastVersionedAt}
                            />
                          </div>
                          {item.businessSegments && item.businessSegments.length > 0 && (
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
                              {item.businessSegments.map((segment) => (
                                <SegmentBadge
                                  key={segment}
                                  segment={segment}
                                  source={item.businessSegmentsSource}
                                />
                              ))}
                            </div>
                          )}
                          {item.tags && item.tags.length > 0 && (
                            <OverflowList
                              items={item.tags}
                              maxVisible={2}
                              getKey={(tag) => tag}
                              renderItem={(tag) => <ItemTagChip tag={tag} />}
                            />
                          )}
                          {/* SC-298: clients this item is assigned to */}
                          {item.clients && item.clients.length > 0 && (
                            <OverflowList
                              items={item.clients}
                              maxVisible={2}
                              getKey={(name) => name}
                              renderItem={(name) => (
                                <span className="chip-tag" title={`Client: ${name}`}>
                                  <Building2 className="h-3 w-3" />
                                  {name}
                                </span>
                              )}
                            />
                          )}
                          {/* SC-300: manually-assigned Release Type */}
                          {item.releaseType && (
                            <span
                              className="chip-tag border-primary/40 text-primary"
                              title={`Release Type: ${item.releaseType}`}
                            >
                              <Layers className="h-3 w-3" />
                              {item.releaseType}
                            </span>
                          )}
                        </div>

                        {item.description && (
                          <ExpandableText
                            text={item.description}
                            className="mt-1.5 text-sm text-muted-foreground"
                          />
                        )}

                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                        >
                          <ExternalLinkIcon className="h-4 w-4" />
                          Read More
                        </a>
                      </div>

                      {/* Always-visible star toggle */}
                      <button
                        type="button"
                        onClick={() => handleMarkAsStarred(item.id, !item.isStarred)}
                        aria-pressed={item.isStarred}
                        aria-label={item.isStarred ? 'Unstar item' : 'Star item'}
                        title={item.isStarred ? 'Unstar' : 'Star'}
                        className="btn-icon flex-shrink-0"
                      >
                        <StarIcon
                          className={cn(
                            'h-4 w-4',
                            item.isStarred && 'fill-warning text-warning'
                          )}
                        />
                      </button>
                    </div>

                    {/* Actions and Comments */}
                    {user && (
                      <FeedItemActions
                        feedItemId={item.id}
                        currentUserId={user.userId}
                        isAdmin={user.isAdmin}
                        isReadOnly={user.role === 'ReadOnly'}
                        isRead={item.isRead}
                        businessSegments={item.businessSegments}
                        businessSegmentsSource={item.businessSegmentsSource}
                        tags={item.tags}
                        releaseType={item.releaseType}
                        versionCount={item.versionCount}
                        isDeleted={item.isDeleted}
                        assignment={item.assignment ?? null}
                        onMarkAsRead={() => handleMarkAsRead(item.id, !item.isRead)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border p-4">
                <div className="text-sm text-muted-foreground">
                  Showing{' '}
                  <span className="font-medium text-foreground tabular-nums">
                    {((currentPage - 1) * pageSize) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-foreground tabular-nums">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-foreground tabular-nums">{totalCount}</span>{' '}
                  results
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                    className="btn-icon disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>

                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          aria-current={page === currentPage ? 'page' : undefined}
                          className={cn(
                            'min-w-[2rem] rounded-md px-2.5 py-1 text-sm font-medium tabular-nums transition-colors',
                            page === currentPage
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          )}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return (
                        <span key={page} className="px-1 text-muted-foreground">
                          …
                        </span>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                    className="btn-icon disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={InboxIcon}
            title="No items found"
            description="Try adjusting your filters or check back later."
          />
        )}
      </div>
    </div>
  );
};
